// Stableford best-net runner — TEAM variant used by tournaments.
//
// Each group is a team. On every hole the best N net Stableford points among
// the group count (N = the tournament's "best net scores to count", 1–4). The
// team's total is the sum across holes; HIGHEST total wins (Stableford is a
// points game). Always full handicap (tournament Stableford has no play-off-low).
//
// Pure standings only (no betting payouts yet). Operates over the whole field.

import { Course, Hole, Player, ScoreMap } from "../types";
import { TeamSegmentScores } from "./teamBets";

function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

// Mirrors pointsForNetScore in stableford.ts — keep in sync.
function pointsForNetScore(
  netStrokes: number,
  par: number,
  scale: "standard" | "modified" | "modifiedPga",
): number {
  const diff = netStrokes - par;
  if (scale === "standard") {
    if (diff >= 2) return 0;
    return Math.min(10, 2 - diff);
  }
  if (scale === "modified") {
    if (diff >= 3) return 0;
    return Math.min(10, 3 - diff);
  }
  // modifiedPga
  if (diff >= 2) return -3;
  if (diff === 1) return -1;
  if (diff === 0) return 0;
  if (diff === -1) return 2;
  if (diff === -2) return 5;
  return 8 + (-2 - diff);
}

export interface StablefordTeamOptions {
  scoringScale?: "standard" | "modified" | "modifiedPga";
  // "best net scores to count" per hole (1–4). Stored as a string by the
  // wizard select; coerced here.
  bestNet?: string | number;
}

export interface StablefordTeamGroupInput {
  groupId: string;
  groupName?: string;
  players: Player[];
  scores: ScoreMap;
}

export interface StablefordTeamInput {
  course: Course;
  groups: StablefordTeamGroupInput[];
  options: StablefordTeamOptions;
}

export interface StablefordTeamResult {
  // Sorted descending — highest points first. `overall` holds team points.
  teams: TeamSegmentScores[];
}

export function runStablefordTeam(
  input: StablefordTeamInput,
): StablefordTeamResult {
  const { course, groups, options } = input;
  const scale = options.scoringScale ?? "standard";
  const n = Math.max(1, Math.min(4, Number(options.bestNet) || 1));
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];

  const teams: TeamSegmentScores[] = groups.map((g) => {
    const hcpById: Record<string, number> = {};
    for (const p of g.players) {
      const tee = tees.find((t) => t.id === p.tee);
      hcpById[p.id] = courseHandicap(
        p.handicap,
        tee?.slope,
        tee?.rating,
        totalPar,
      );
    }

    let overall = 0;
    let front = 0;
    let back = 0;
    let holesPlayed = 0;
    for (const hole of course.holes) {
      const pts: number[] = [];
      for (const p of g.players) {
        const sc = (g.scores[p.id] || {})[hole.number];
        if (!sc || typeof sc.gross !== "number") continue;
        const net = sc.gross - strokesOnHole(hcpById[p.id], hole);
        pts.push(pointsForNetScore(net, hole.par, scale));
      }
      if (pts.length === 0) continue;
      holesPlayed++;
      pts.sort((a, b) => b - a); // highest first
      const take = Math.min(n, pts.length);
      let contrib = 0;
      for (let i = 0; i < take; i++) contrib += pts[i];
      overall += contrib;
      if (hole.number <= 9) front += contrib;
      else back += contrib;
    }

    return {
      groupId: g.groupId,
      groupName: g.groupName,
      players: g.players,
      overall,
      front,
      back,
      holesPlayed,
    };
  });

  // Highest points win, but teams that haven't started (no holes played,
  // 0 points) sort to the bottom rather than tying at the top.
  teams.sort((a, b) => {
    if (a.holesPlayed === 0 && b.holesPlayed === 0) return 0;
    if (a.holesPlayed === 0) return 1;
    if (b.holesPlayed === 0) return -1;
    return b.overall - a.overall;
  });
  return { teams };
}
